import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/flexible/Paginated';
import http from 'services/http';

// A run's own query sits apart from the lists so a settled run can refresh the lists without
// touching itself: the show query evaluates its poll interval on every refetch, so a refresh that
// included it would re-enter that callback at once. Items sit apart too: a settled run's items do
// not change, so a list refresh should not refetch every run's items again.
export const adminPccRetroactiveDischargeRunsQueryKeys = {
  lists: ['adminPccRetroactiveDischargeRuns', 'lists'] as const,
  index: (clientId: string, params?: Record<string, any>) =>
    ['adminPccRetroactiveDischargeRuns', 'lists', 'client', clientId, params] as const,
  items: (runId: string) => ['adminPccRetroactiveDischargeRuns', 'items', runId] as const,
  show: (id: string) => ['adminPccRetroactiveDischargeRuns', 'run', id] as const,
};

const URL = 'admin/pcc_retroactive_discharge_runs';

export type RetroactiveDischargeRunMode = 'preview' | 'live';

// preview: finding_patients -> ready_for_review. live: running <-> paused -> done. Either can end failed.
export type RetroactiveDischargeRunStatus =
  | 'finding_patients'
  | 'ready_for_review'
  | 'running'
  | 'paused'
  | 'done'
  | 'failed';

export const TERMINAL_RUN_STATUSES: RetroactiveDischargeRunStatus[] = ['ready_for_review', 'done', 'failed'];

export type RetroactiveDischargeRunAction = 'pause' | 'resume' | 'rerun';

// toSend + sent + processed === total; processed is the sum of the outcome counts.
export type RetroactiveDischargeRunCounts = {
  total: number;
  toSend: number;
  sent: number;
  processed: number;
  discharged: number;
  wouldDischarge: number;
  wouldDischargeNeedsReview: number;
  skipped: number;
  noResponse: number;
};

export type RetroactiveDischargeRun = {
  id: string;
  clientId: string;
  mode: RetroactiveDischargeRunMode;
  status: RetroactiveDischargeRunStatus;
  includeUnflagged: boolean;
  pageSize: number;
  sourceRunId: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  triggeredBy: { id: string; name: string };
  counts: RetroactiveDischargeRunCounts;
  // The highest wave number on the run's items; null before the first wave goes out
  currentWave: number | null;
  // Still finding patients or running, but no wave has moved past the wave timeout; re-run picks it up
  stalled: boolean;
};

export type RetroactiveDischargeItemStatus = 'pending' | 'sent' | 'resolved';

export type RetroactiveDischargeItemOutcome =
  | 'discharged'
  | 'would_discharge'
  | 'would_discharge_needs_review'
  | 'skipped'
  | 'no_response';

export type RetroactiveDischargeRunItem = {
  id: string;
  locationEpisodeId: string;
  episodeId: string;
  encounterId: string;
  wave: number | null;
  status: RetroactiveDischargeItemStatus;
  outcome: RetroactiveDischargeItemOutcome | null;
  reason: string | null;
  // As PointClickCare sent it: a date, or a timestamp with its offset
  dischargedAt: string | null;
  // The facility's date for that discharge (yyyy-MM-dd), the date Olio discharges on. Show this, not dischargedAt.
  dischargeDate: string | null;
  dispositionCode: string | null;
  // PointClickCare's own text when it sent one, else the code's display label
  dispositionText: string | null;
  dispositionDisplay: string | null;
  death: boolean;
  destinationName: string | null;
  destinationType: string | null;
  detailsSavedAt: string | null;
  sentAt: string | null;
  resolvedAt: string | null;
  admittedAt: string | null;
  // Unchecked on the review list: a live run refuses to include it
  leftForStaff: boolean;
  patient: { id: string; name: string };
  facility: { id: string; name: string };
};

export type AdminCreatePreviewRunParams = { mode: 'preview'; includeUnflagged: boolean; pageSize?: number };
export type AdminCreateLiveRunParams = { mode: 'live'; sourceRunId: string; itemIds: string[]; pageSize?: number };

export function isTerminalRun(run: Pick<RetroactiveDischargeRun, 'status'>) {
  return TERMINAL_RUN_STATUSES.includes(run.status);
}

export async function adminIndexPccRetroactiveDischargeRuns(
  clientId: string,
  params: { page?: number; perPage?: number } = {},
  signal?: GenericAbortSignal
): Promise<Paginated<RetroactiveDischargeRun>> {
  return http
    .get<Paginated<RetroactiveDischargeRun>>(`admin/clients/${clientId}/pcc_retroactive_discharge_runs`, {
      params,
      signal,
    })
    .then((res) => res.data);
}

export async function adminCreatePccRetroactiveDischargeRun(
  clientId: string,
  params: AdminCreatePreviewRunParams | AdminCreateLiveRunParams
): Promise<RetroactiveDischargeRun> {
  return http
    .post<RetroactiveDischargeRun>(`admin/clients/${clientId}/pcc_retroactive_discharge_runs`, params)
    .then((res) => res.data);
}

export async function adminShowPccRetroactiveDischargeRun(
  id: string,
  signal?: GenericAbortSignal
): Promise<RetroactiveDischargeRun> {
  return http.get<RetroactiveDischargeRun>(`${URL}/${id}`, { signal }).then((res) => res.data);
}

export async function adminInvokePccRetroactiveDischargeRunAction(
  id: string,
  action: RetroactiveDischargeRunAction
): Promise<RetroactiveDischargeRun> {
  return http.patch<RetroactiveDischargeRun>(`${URL}/${id}`, { invokeAction: action }).then((res) => res.data);
}

export async function adminIndexPccRetroactiveDischargeRunItems(
  runId: string,
  params: { page?: number; perPage?: number } = {},
  signal?: GenericAbortSignal
): Promise<Paginated<RetroactiveDischargeRunItem>> {
  return http
    .get<Paginated<RetroactiveDischargeRunItem>>(`${URL}/${runId}/items`, { params, signal })
    .then((res) => res.data);
}

const ITEMS_PAGE_SIZE = 500;

// The review list is the whole run, not a page of it: every row is a decision, and the checkboxes
// and "Discharge N selected" count only make sense over all of them.
export async function adminIndexAllPccRetroactiveDischargeRunItems(
  runId: string,
  signal?: GenericAbortSignal
): Promise<RetroactiveDischargeRunItem[]> {
  const first = await adminIndexPccRetroactiveDischargeRunItems(runId, { page: 1, perPage: ITEMS_PAGE_SIZE }, signal);
  const rest = await Promise.all(
    Array.from({ length: Math.max(first.meta.totalPages - 1, 0) }, (_, i) =>
      adminIndexPccRetroactiveDischargeRunItems(runId, { page: i + 2, perPage: ITEMS_PAGE_SIZE }, signal)
    )
  );
  return [first, ...rest].flatMap((page) => page.data);
}

export async function adminSetPccRetroactiveDischargeRunItemsLeftForStaff(
  runId: string,
  params: { itemIds: string[]; leftForStaff: boolean }
): Promise<{ updated: number }> {
  return http.patch<{ updated: number }>(`${URL}/${runId}/items`, params).then((res) => res.data);
}
